/**
 * Floating text system — shows damage numbers, resource gains, etc.
 * Texts float upward and fade out, then self-destruct.
 */

import { Container, Text, TextStyle } from 'pixi.js';

interface FloatingTextData {
  textObj: Text;
  vy: number;
  alpha: number;
  life: number;
}

const BASE_VY = 40; // pixels per second
const BASE_LIFE = 1.5; // seconds

class FloatingText {
  container: Container;
  private texts: FloatingTextData[] = [];

  constructor() {
    this.container = new Container();
    this.container.sortableChildren = true;
  }

  /**
   * Show a floating text at world coordinates.
   * @param text - The text to display
   * @param worldX - World X position
   * @param worldY - World Y position
   * @param color - Text color as hex number (e.g. 0xff4444)
   */
  show(text: string, worldX: number, worldY: number, color: number): void {
    const style = new TextStyle({
      fontFamily: 'monospace',
      fontSize: 14,
      fill: color,
      fontWeight: 'bold',
      dropShadow: {
        alpha: 0.8,
        color: 0x000000,
        blur: 2,
        distance: 1,
      },
    });

    const textObj = new Text({ text, style });
    textObj.anchor.set(0.5, 0.5);
    textObj.x = worldX;
    textObj.y = worldY;

    this.container.addChild(textObj);
    this.texts.push({
      textObj,
      vy: BASE_VY,
      alpha: 1.0,
      life: BASE_LIFE,
    });
  }

  /**
   * Update all floating texts — move upward and fade out.
   * @param dt - Delta time in seconds
   */
  update(dt: number): void {
    for (let i = this.texts.length - 1; i >= 0; i--) {
      const ft = this.texts[i];
      ft.textObj.y -= ft.vy * dt;
      ft.life -= dt;
      ft.alpha = Math.max(0, ft.life / BASE_LIFE);
      ft.textObj.alpha = ft.alpha;

      if (ft.alpha <= 0) {
        this.container.removeChild(ft.textObj);
        ft.textObj.destroy();
        this.texts.splice(i, 1);
      }
    }
  }
}

/** Global singleton instance. */
export const floatingText = new FloatingText();
